import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, ShoppingBag, Megaphone, Truck, ShoppingCart, Briefcase, MapPin,
  ChevronRight, Folder, FileText, Pencil,
} from "lucide-react";

const ICONS = {
  Users, ShoppingBag, Megaphone, Truck, ShoppingCart, Briefcase, MapPin, Folder,
};

function CategoryCard({ category, idx }) {
  const Icon = ICONS[category.icon] || Folder;
  const accent = category.color || "#1B2E7A";
  return (
    <Link
      to={`/procesos/${category.slug}`}
      data-testid={`process-cat-${category.slug}`}
      className={`fade-up stagger-${(idx % 6) + 1} group bg-white border border-[#E5E7EB] rounded-md overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col`}
    >
      <div
        className="relative aspect-[5/3] flex flex-col justify-between p-5 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}D9 100%)` }}
      >
        <div
          className="absolute inset-0 opacity-15 mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6), transparent 40%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.4), transparent 50%)",
          }}
        />
        <div className="relative z-10 flex items-start justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70">Categoría</p>
          <Icon className="h-7 w-7 text-white/90" strokeWidth={1.4} />
        </div>
        <div className="relative z-10">
          <h3 className="font-heading text-2xl text-white font-medium leading-tight">
            {category.name}
          </h3>
          {category.tagline && (
            <p className="text-xs text-white/80 mt-1 italic">{category.tagline}</p>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-sm text-[#374151] leading-relaxed line-clamp-3 flex-1">{category.description}</p>
        <div className="flex items-center justify-between mt-4 text-xs">
          <span className="inline-flex items-center gap-1 text-[#737373]">
            <FileText className="h-3.5 w-3.5" />
            {category.resources_count || 0} {category.resources_count === 1 ? "material" : "materiales"}
          </span>
          <span className="inline-flex items-center gap-1 text-[#1B2E7A] font-medium group-hover:gap-2 transition-all">
            Abrir <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ProcessCategories() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/process-categories");
        setCats(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="process-categories-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
          <Folder className="h-3.5 w-3.5" /> Procesos por departamento
        </p>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
              Procesos del equipo Canales.
            </h1>
            <p className="text-[#374151] max-w-2xl leading-relaxed">
              Procedimientos, checklists, videos y manuales organizados por área. Cada categoría reúne
              los materiales que el equipo necesita para operar con estándares claros en piso, almacén,
              oficina y comunidad.
            </p>
          </div>
          {user?.role === "admin" && (
            <Link
              to="/admin/procesos"
              data-testid="open-admin-processes"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1B2E7A] text-white text-sm hover:bg-[#13205A]"
            >
              <Pencil className="h-4 w-4" /> Gestionar materiales
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-[#374151]">Cargando categorías...</p>
      ) : cats.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-md">
          <Folder className="h-10 w-10 text-[#6B7280] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#374151]">Aún no hay categorías.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cats.map((c, i) => (
            <CategoryCard key={c.id} category={c} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}
